import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A proxy button component that routes clicks to the main add-to-cart button.
 * This allows placing ATC functionality anywhere on the PDP.
 *
 * @typedef {object} ProxyAddToCartRefs
 * @property {HTMLButtonElement} proxyButton - The proxy button element.
 * @extends {Component<ProxyAddToCartRefs>}
 */
class ProxyAddToCartButton extends Component {
  requiredRefs = ['proxyButton'];

  /** @type {HTMLButtonElement | null} */
  #targetButton = null;

  connectedCallback() {
    super.connectedCallback();
    this.#syncState();

    // Listen for variant updates to sync disabled state
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    window.addEventListener('resize', this.#onViewportChange, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    window.removeEventListener('resize', this.#onViewportChange);
  }

  /**
   * Cache reference to the target add-to-cart button.
   * If multiple buttons exist (e.g. desktop + mobile), prefer the visible one.
   */
  #cacheTargetButton() {
    const allTargets = Array.from(document.querySelectorAll('[ref="addToCartButton"]')).filter(
      (el) => el instanceof HTMLButtonElement
    );

    /** @param {HTMLButtonElement} el */
    const isVisible = (el) => {
      if (el.disabled) return true; // still "visible" enough for state sync; we filter by geometry below
      const style = window.getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      // If hidden by ancestor display:none, rects will be empty.
      return el.getClientRects().length > 0;
    };

    const visibleTargets = allTargets.filter((el) => isVisible(el));

    // Prefer an enabled visible target; otherwise the first visible target; otherwise fall back to the first target.
    this.#targetButton =
      visibleTargets.find((el) => !el.disabled) ?? visibleTargets[0] ?? (allTargets[0] ?? null);
  }

  /**
   * Sync the proxy button state with the target button
   */
  #syncState() {
    this.#cacheTargetButton();

    if (!this.refs.proxyButton) return;

    if (!this.#targetButton) {
      this.refs.proxyButton.disabled = true;
      return;
    }

    this.refs.proxyButton.disabled = this.#targetButton.disabled;
  }

  /**
   * Handle variant updates to sync state
   */
  #onVariantUpdate = () => {
    // Re-cache and sync after DOM updates
    requestAnimationFrame(() => {
      this.#syncState();
    });
  };

  /**
   * Handle viewport changes (desktop/mobile toggle) to ensure we click the visible ATC.
   */
  #onViewportChange = () => {
    requestAnimationFrame(() => {
      this.#syncState();
    });
  };

  /**
   * Handles the click event by triggering the main add-to-cart button.
   *
   * @param {Event} event - The click event
   */
  handleClick(event) {
    event.preventDefault();

    // Ensure we target the currently-visible button (desktop/mobile can swap)
    this.#cacheTargetButton();

    if (this.#targetButton && !this.#targetButton.disabled) {
      this.#targetButton.click();
    }
  }
}

if (!customElements.get('proxy-add-to-cart')) {
  customElements.define('proxy-add-to-cart', ProxyAddToCartButton);
}
